import type {
  AgentRuntimeErrorItem,
  AgentRuntimeMessageAttachment,
  AgentRuntimeTimelineItem,
  AgentRuntimeToolCallItem,
  AgentRuntimeUsageState,
  AgentRuntimeUsageTotals,
} from "@cord/shared"
import { normalizeAgentErrorMessage } from "@cord/shared"

export type MessageEntry = {
  info: Record<string, unknown>
  partsById: Record<string, Record<string, unknown>>
}

export type ChildSessionToolPartsBySessionId = Record<string, Record<string, Record<string, unknown>>>
export type TaskChildLink = { parentToolCallId: string; startedAt: number }

export type OpenCodeChildSessionRuntimeState = {
  statusType?: string | null
  lastEventAt?: number | null
  finishReason?: string | null
  errorMessage?: string | null
  errorType?: string | null
}

export type OpenCodeTaskLinkIndexEntry = {
  messageIds: string[]
  parentChildSessionIds: string[]
}

export type OpenCodeProjectionGraph = {
  messagesById: Record<string, MessageEntry>
  childSessionToolPartsBySessionId: ChildSessionToolPartsBySessionId
  childSessionStateBySessionId?: Record<string, OpenCodeChildSessionRuntimeState>
  sessionErrors?: AgentRuntimeErrorItem[]
  taskLinkIndexByChildSessionId?: Record<string, OpenCodeTaskLinkIndexEntry>
  taskLinkIndexValid?: boolean
}

export type OpenCodeDerivedState = {
  derivedStateVersion: number
  orderedMessageIds: string[]
  messageTimelineItemsById: Record<string, AgentRuntimeTimelineItem[]>
  orderedChildSessionIds: string[]
  childSessionTimelineItemsBySessionId: Record<string, AgentRuntimeToolCallItem[]>
  timeline: AgentRuntimeTimelineItem[]
  usageByMessageId: Record<string, AgentRuntimeUsageTotals | null>
  usage: AgentRuntimeUsageState
}

export type OpenCodeBatchMutationState = {
  dirtyMessageIds: Set<string>
  dirtyChildSessionIds: Set<string>
  messageOrderDirty: boolean
  childSessionOrderDirty: boolean
  timelineDirty: boolean
  taskLinksDirty: boolean
  usageDirty: boolean
}

// Bump this when persisted OpenCode projection caches need a full derived-state
// rebuild, including semantic changes in task-link association logic.
// Version 9 closes orphaned older assistant turns once a newer assistant turn
// exists, so cached browser projections cannot keep stale thinking shimmer state
// alive after later replies have already landed.
// Version 10 adds defensive normalization of audit-synthetic session.status
// events (injects terminal finishedAt / runtimeFinality markers when the
// server only emitted {type: "idle"}). Forces a rebuild so persisted
// projections that were built under the old semantics re-evaluate streaming
// state via the new normalization on hydrate.
// Version 11 canonicalizes dangling pending/running non-task tools from
// completed or non-latest assistant turns so stale tool parts cannot keep a
// hydrated OpenCode snapshot in phase="running" after the run has advanced.
export const OPENCODE_DERIVED_STATE_VERSION = 11

export function createEmptyOpenCodeDerivedState(): OpenCodeDerivedState {
  return {
    derivedStateVersion: OPENCODE_DERIVED_STATE_VERSION,
    orderedMessageIds: [],
    messageTimelineItemsById: {},
    orderedChildSessionIds: [],
    childSessionTimelineItemsBySessionId: {},
    timeline: [],
    usageByMessageId: {},
    usage: {},
  }
}

export function createOpenCodeBatchMutationState(): OpenCodeBatchMutationState {
  return {
    dirtyMessageIds: new Set(),
    dirtyChildSessionIds: new Set(),
    messageOrderDirty: false,
    childSessionOrderDirty: false,
    timelineDirty: false,
    taskLinksDirty: false,
    usageDirty: false,
  }
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

export function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function normalizeTimestamp(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  const normalized = Math.floor(value)
  return normalized < 10_000_000_000 ? normalized * 1000 : normalized
}

export function normalizeTokenValue(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0
  return Math.max(0, value)
}

export function resolveTimestamp(...values: unknown[]): number {
  for (const value of values) {
    const ts = normalizeTimestamp(value)
    if (ts !== null) return ts
  }
  return Date.now()
}

export function resolveOpenCodeMessageOrderingTimestamp(args: {
  message: MessageEntry
}): number | null {
  const info = asRecord(args.message.info)
  const time = asRecord(info?.time)
  const createdAt = normalizeTimestamp(time?.created)
  if (createdAt !== null) return createdAt

  let earliestPartTimestamp: number | null = null
  for (const part of Object.values(args.message.partsById)) {
    const partTime = asRecord(part.time)
    const stateTime = asRecord(asRecord(part.state)?.time)
    const candidateTimestamp =
      normalizeTimestamp(partTime?.start) ??
      normalizeTimestamp(stateTime?.start) ??
      normalizeTimestamp(partTime?.end) ??
      normalizeTimestamp(stateTime?.end)
    if (candidateTimestamp === null) continue
    if (earliestPartTimestamp === null || candidateTimestamp < earliestPartTimestamp) {
      earliestPartTimestamp = candidateTimestamp
    }
  }

  if (earliestPartTimestamp !== null) return earliestPartTimestamp
  return normalizeTimestamp(time?.completed)
}

export function normalizeProviderStatus(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === "string") return { type: raw }
  const record = asRecord(raw)
  if (!record) return null

  const nextRecord = { ...record }
  const normalizedMessage = normalizeAgentErrorMessage({
    value: record.message,
  })
  if (normalizedMessage) {
    nextRecord.message = normalizedMessage
  }

  return nextRecord
}

export function extractActorMetadata(raw: unknown): AgentRuntimeTimelineItem["actor"] {
  const source = asRecord(raw)
  if (!source) return undefined
  const actorRaw = asRecord(source.cordActor) ?? source
  const userId = asString(actorRaw.userId)
  const name = asString(actorRaw.name)
  const email = asString(actorRaw.email)
  const orgRole =
    actorRaw.orgRole === "owner" || actorRaw.orgRole === "admin" || actorRaw.orgRole === "member"
      ? actorRaw.orgRole
      : null
  const sourceType = actorRaw.source === "session_ws" || actorRaw.source === "remote_surface"
    ? actorRaw.source
    : null
  const submittedAt = normalizeTimestamp(actorRaw.submittedAt)
  if (!userId && !name && !email && !orgRole && !sourceType && submittedAt === null) {
    return undefined
  }
  return {
    userId,
    name,
    email,
    orgRole,
    source: sourceType,
    submittedAt,
  }
}

export function buildAttachment(input: {
  id: string
  url: string
  filename?: string | null
  mime?: string | null
}): AgentRuntimeMessageAttachment {
  return {
    id: input.id,
    url: input.url,
    filename: input.filename ?? undefined,
    mime: input.mime ?? undefined,
  }
}

export function isTaskToolPart(part: Record<string, unknown> | null | undefined): boolean {
  return part?.type === "tool" && part.tool === "task"
}

export function shouldPromoteBusyFromAssistantPart(part: Record<string, unknown>): boolean {
  const partType = asString(part.type)?.toLowerCase()
  const partTime = asRecord(part.time)
  const state = asRecord(part.state)
  const stateTime = asRecord(state?.time)
  const stateStatus = asString(state?.status)?.toLowerCase()

  if (partType === "step-finish") {
    return false
  }

  if (partType === "tool") {
    if (stateStatus === "pending" || stateStatus === "running") {
      return true
    }

    if (stateStatus === "completed" || stateStatus === "error") {
      return false
    }

    return normalizeTimestamp(stateTime?.end) === null
      && normalizeTimestamp(partTime?.end) === null
  }

  return normalizeTimestamp(partTime?.end) === null
}

export function isAssistantOnlyPartType(partType: string | null): boolean {
  return partType === "reasoning"
    || partType === "tool"
    || partType === "step-start"
    || partType === "step-finish"
    || partType === "patch"
}

export function messageHasOpenAssistantPart(partsById: MessageEntry["partsById"]): boolean {
  return Object.values(partsById).some((part) => {
    const partType = asString(part.type)?.toLowerCase() ?? null
    if (partType !== "text" && !isAssistantOnlyPartType(partType)) {
      return false
    }

    return shouldPromoteBusyFromAssistantPart(part)
  })
}

function textualAssistantPartHasVisibleContent(args: {
  part: Record<string, unknown>
}): boolean {
  return typeof args.part.text === "string" && args.part.text.trim().length > 0
}

export function messageHasOpenRenderableAssistantPart(args: {
  partsById: MessageEntry["partsById"]
}): boolean {
  return Object.values(args.partsById).some((part) => {
    const partType = asString(part.type)?.toLowerCase() ?? null
    if (partType === "text" || partType === "reasoning") {
      return textualAssistantPartHasVisibleContent({ part })
        && shouldPromoteBusyFromAssistantPart(part)
    }

    if (!isAssistantOnlyPartType(partType)) {
      return false
    }

    return shouldPromoteBusyFromAssistantPart(part)
  })
}

export function messageContainsTaskTool(message: MessageEntry | null | undefined): boolean {
  if (!message) return false
  return Object.values(message.partsById).some((part) => isTaskToolPart(part))
}

export function normalizeToolStatus(rawStatus: string | null): AgentRuntimeToolCallItem["status"] {
  if (
    rawStatus === "pending" ||
    rawStatus === "running" ||
    rawStatus === "completed" ||
    rawStatus === "error"
  ) {
    return rawStatus
  }
  return "running"
}

export function buildUsageTotalsFromOpenCodeTokens(raw: unknown): AgentRuntimeUsageTotals | null {
  const tokens = asRecord(raw)
  if (!tokens) return null
  const cache = asRecord(tokens.cache)

  const inputTokens = normalizeTokenValue(tokens.input ?? tokens.inputTokens)
  const outputTokens = normalizeTokenValue(tokens.output ?? tokens.outputTokens)
  const reasoningTokens = normalizeTokenValue(tokens.reasoning ?? tokens.reasoningTokens)
  const cacheReadTokens = normalizeTokenValue(
    cache?.read ??
      tokens.cacheReadTokens ??
      tokens.cache_read_input_tokens,
  )
  const cacheWriteTokens = normalizeTokenValue(
    cache?.write ??
      tokens.cacheWriteTokens ??
      tokens.cache_creation_input_tokens,
  )

  const computedUsedTokens =
    inputTokens + outputTokens + reasoningTokens + cacheReadTokens + cacheWriteTokens
  const usedTokens = Math.max(normalizeTokenValue(tokens.usedTokens), computedUsedTokens)
  if (usedTokens <= 0) return null

  return {
    inputTokens,
    outputTokens,
    reasoningTokens,
    cacheReadTokens,
    cacheWriteTokens,
    usedTokens,
  }
}
